import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import Skeleton from '../../components/Skeleton';
import { fmtBarDate, TRADE_ACTION_COLORS } from './utils';

// Isolated memo component so crosshair updates only re-render the equity chart,
// not the stat cards, correlation charts, trade log, etc.
const EquityCurveChart = React.memo(function EquityCurveChart({ equityCurve, crosshairDate }) {
  const hasBenchmark = equityCurve.some((pt) => pt.benchmark != null);
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={equityCurve} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted)' }} interval="preserveStartEnd" tickFormatter={fmtBarDate} />
        <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} domain={['auto', 'auto']} />
        <Tooltip
          formatter={(val, name) => ['$' + Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 }), name]}
          contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)' }}
        />
        {hasBenchmark && <Legend />}
        <Line type="monotone" dataKey="value" stroke="var(--teal)" dot={false} name="Strategy" strokeWidth={2} isAnimationActive={false} />
        {hasBenchmark && (
          <Line type="monotone" dataKey="benchmark" stroke="var(--muted)" dot={false} name="Buy & Hold" strokeWidth={1.5} strokeDasharray="5 4" isAnimationActive={false} connectNulls />
        )}
        {crosshairDate && (
          <ReferenceLine x={crosshairDate} stroke="var(--muted)" strokeWidth={1} strokeDasharray="4 3" />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
});

export default function EquityCurveSection({ backtest, crosshairDate }) {
  const [tradeLogOpen, setTradeLogOpen] = useState(false);

  const equityCurve = backtest.equity_curve || [];
  if (equityCurve.length === 0) {
    if (backtest.status === 'running' || backtest.status === 'pending') {
      return (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Skeleton width={120} height={20} style={{ marginBottom: 'var(--space-2)' }} />
          <Skeleton height={260} />
        </div>
      );
    }
    return null;
  }

  const isFx = backtest.asset_type === 'fx';
  const tradeEvents = backtest.trade_events || [];
  const fmtPrice = (v) => v == null ? '—' : isFx ? Number(v).toFixed(5) : '$' + Number(v).toFixed(2);

  return (
    <>
      <h3 style={{ color: 'var(--text)' }}>Equity Curve</h3>
      <EquityCurveChart equityCurve={equityCurve} crosshairDate={crosshairDate} />

      {/* Trade log – collapsible */}
      {tradeEvents.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text)' }}>
          <button
            onClick={() => setTradeLogOpen((o) => !o)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text)', fontWeight: 700, fontSize: 12,
              padding: 0, display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <span style={{ fontSize: 10 }}>{tradeLogOpen ? '▼' : '▶'}</span>
            Trade Log ({tradeEvents.length} execution{tradeEvents.length !== 1 ? 's' : ''})
          </button>
          {tradeLogOpen && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, maxHeight: 240, overflowY: 'auto', paddingRight: 4 }}>
              {tradeEvents.map((t, i) => {
                const color = TRADE_ACTION_COLORS[t.action] || '#8b949e';
                const reasonLabel = t.reason === 'stop_loss' ? 'Stop Loss'
                  : t.reason === 'take_profit' ? 'Take Profit'
                  : null;
                return (
                  <span key={i} style={{
                    padding: '3px 8px', borderRadius: 4, fontSize: 11,
                    fontWeight: 600, fontFamily: 'var(--font-mono)',
                    background: `${color}26`,
                    color,
                  }}>
                    {t.action} @ {fmtPrice(t.price)} ({fmtBarDate(t.date)}){reasonLabel ? ` · ${reasonLabel}` : ''}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
}
